import logoDark from "@public/logo.svg";
import logoLight from "@public/logo_on_light.svg";
import type { TenantInfo } from "@repo/shared/types";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { OrganizationCard } from "@/components/auth/organization-card";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@/components/ui/link";
import { getTenantBySlug, requestPasswordResetWithTenant } from "@/lib/api/auth-login";
import { useT } from "@/lib/i18n/use-t";
import { forgetOrgSlug, getRememberedOrgSlug, rememberOrgSlug } from "@/lib/remembered-org";
import { getTenantHostContext, switchOrganizationUrl } from "@/lib/tenant-host";
import { T } from "./page.i18n";

// Logo + title card shared by every state of the page
function PageShell({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-md">
        <Card className="w-full p-8">
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-4">
              {/* Show light mode logo by default, dark mode logo when .dark class is present */}
              <img
                src={logoLight}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="dark:hidden"
              />
              <img
                src={logoDark}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="hidden dark:block"
              />
            </div>
            <h1 className="text-2xl font-bold">{title}</h1>
            {description && <p className="text-muted-foreground mt-2">{description}</p>}
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

/**
 * Forgot-password is tenant-scoped (QL-245): the same email can exist in
 * several organizations, so the organization is resolved first — exactly like
 * the sign-in form — and the email step only appears once it is known.
 * `?tenant=<slug>` (from the sign-in form's link) or the remembered
 * organization preselects it.
 */
export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  // QL-343 — on a tenant host the organization is the host itself
  const hostContext = useMemo(getTenantHostContext, []);
  const hostSlug = hostContext.kind === "tenant" ? hostContext.slug : null;
  const t = useT(T);
  const [step, setStep] = useState<"tenant" | "email">("tenant");
  const [tenantSlug, setTenantSlug] = useState(() => hostSlug ?? getRememberedOrgSlug() ?? "");
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [isTenantLoading, setIsTenantLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const urlTenant = searchParams.get("tenant")?.toLowerCase().trim() || null;
  const [isRestoringOrg, setIsRestoringOrg] = useState(
    () => hostSlug !== null || urlTenant !== null || getRememberedOrgSlug() !== null,
  );
  // True only when the organization came from the remembered slug, not from
  // the URL or manual entry
  const [isRestoredOrg, setIsRestoredOrg] = useState(false);

  useEffect(() => {
    // Strip the tenant hint from the URL (replaceState keeps searchParams
    // stable so the in-flight lookup below is not cancelled)
    if (window.location.search) window.history.replaceState({}, "", "/forgot-password");

    const remembered = hostSlug || urlTenant ? null : getRememberedOrgSlug();
    const slug = hostSlug ?? urlTenant ?? remembered;
    if (!slug) return;

    let cancelled = false;
    getTenantBySlug(slug)
      .then((tenant) => {
        if (cancelled) return;
        if (tenant) {
          // QL-351 — an old slug resolves to the renamed organization's current one
          rememberOrgSlug(tenant.slug);
          setTenantSlug(tenant.slug);
          setTenantInfo(tenant);
          setIsRestoredOrg(remembered !== null);
          setStep("email");
        } else if (remembered) {
          forgetOrgSlug();
          setTenantSlug("");
        } else {
          setTenantSlug(slug);
          setError(t.orgNotFound);
        }
      })
      .catch(() => {
        setTenantSlug(slug);
      })
      .finally(() => {
        if (!cancelled) setIsRestoringOrg(false);
      });
    return () => {
      cancelled = true;
    };
    // Runs once on mount: the URL is read before it is cleaned above
  }, []);

  const handleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isTenantLoading) return;
    setError(null);

    if (!tenantSlug.trim()) {
      setError(t.enterOrgIdentifier);
      return;
    }

    setIsTenantLoading(true);
    try {
      const normalizedSlug = tenantSlug.toLowerCase().trim();
      const tenant = await getTenantBySlug(normalizedSlug);

      if (!tenant) {
        setError(t.orgNotFound);
        return;
      }

      rememberOrgSlug(tenant.slug);
      setTenantSlug(tenant.slug);
      setTenantInfo(tenant);
      setIsRestoredOrg(false);
      setStep("email");
    } finally {
      setIsTenantLoading(false);
    }
  };

  const handleChangeOrganization = () => {
    forgetOrgSlug();
    // On a tenant host another organization is picked on the root domain
    if (hostSlug) {
      window.location.href = switchOrganizationUrl();
      return;
    }
    setTenantSlug("");
    setTenantInfo(null);
    setIsRestoredOrg(false);
    setStep("tenant");
    setError(null);
    setEmail("");
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSending) return;
    setError(null);

    if (!email.trim()) {
      setError(t.enterYourEmail);
      return;
    }

    setIsSending(true);
    try {
      const result = await requestPasswordResetWithTenant({ email: email.trim(), tenantSlug });
      if (!result.success) {
        setError(result.error || t.failedToSendReset);
        return;
      }
      setSuccess(true);
    } finally {
      setIsSending(false);
    }
  };

  if (success) {
    return (
      <PageShell
        title={t.checkYourEmail}
        description={t.ifAccountExists(email.trim(), tenantInfo?.name ?? tenantSlug)}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">{t.didntReceiveEmail}</p>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setSuccess(false);
              setEmail("");
            }}
          >
            {t.tryAgain}
          </Button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-primary hover:underline">
              {t.backToLogin}
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  if (isRestoringOrg) {
    return (
      <PageShell title={t.forgotPassword}>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageShell>
    );
  }

  if (step === "tenant") {
    return (
      <PageShell title={t.forgotPassword} description={t.enterOrgFirst}>
        <form onSubmit={handleTenantSubmit} className="space-y-4">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
          )}

          <div className="space-y-2">
            <Label htmlFor="tenantSlug">
              {t.organizationIdentifier} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="tenantSlug"
              type="text"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              placeholder="your-organization"
              autoFocus
              disabled={isTenantLoading}
            />
            <p className="text-xs text-muted-foreground">{t.enterOrgUniqueId}</p>
          </div>

          <Button type="submit" className="w-full" disabled={isTenantLoading}>
            {isTenantLoading ? t.checking : t.continue}
          </Button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-primary hover:underline">
              {t.backToLogin}
            </Link>
          </div>
        </form>
      </PageShell>
    );
  }

  return (
    <PageShell title={t.forgotPassword} description={t.enterEmailForReset}>
      <div className="space-y-4">
        <OrganizationCard
          name={tenantInfo?.name}
          isRestored={isRestoredOrg}
          onChange={handleChangeOrganization}
        />

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
        )}

        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">
              {t.email} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus
              disabled={isSending}
            />
          </div>

          {/* SSO users have no password here — the backend refuses them, this just says why */}
          {tenantInfo?.ssoEnabled && (
            <p className="text-xs text-muted-foreground">
              {t.ssoPasswordNote(tenantInfo.ssoName || "SSO")}
            </p>
          )}

          <Button type="submit" className="w-full" loading={isSending}>
            {t.sendResetLink}
          </Button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-primary hover:underline">
              {t.backToLogin}
            </Link>
          </div>
        </form>
      </div>
    </PageShell>
  );
}
